import React from "react";

import { FormControlLabel, ToggleButton, Typography } from "@mui/material";

import useTranscriptStore, {
  TextSize,
} from "@app/analytics/MeetingReport/stores/transcript";
import useVideoStore from "@app/analytics/MeetingReport/stores/video";
import useUserMetadata from "@app/common/auth/useUserMetadata";

import { MenuDivider } from "@rui/Menu";
import Stack, { Row } from "@rui/Stack";
import Switch from "@rui/Switch";
import TooltipIcon from "@rui/TooltipIcon";

import { SwitchLabel, ToggleButtonGroup } from "./styles";

const RecapSettingsContent = () => {
  const { userMetadata, updateUserMetadata } = useUserMetadata();

  const { autoPlay, playbackEnabled, toggleAutoPlay } = useVideoStore(
    (state) => ({
      autoPlay: state.autoPlay,
      playbackEnabled: state.playbackEnabled,
      toggleAutoPlay: state.toggleAutoPlay,
    })
  );

  const { textSize, setTextSize, reactionsEnabled, toggleReactionsEnabled } =
    useTranscriptStore((state) => ({
      textSize: state.textSize,
      setTextSize: state.setTextSize,
      reactionsEnabled: state.reactionsEnabled,
      toggleReactionsEnabled: state.toggleReactionsEnabled,
    }));

  const handleTextSizeChange = (
    event: React.MouseEvent<HTMLElement>,
    size: string
  ) => {
    if (size) {
      setTextSize(size as TextSize);
    }
  };

  const toggleAutoPlayOnLoad = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    updateUserMetadata.mutate({ autoPlayMeetingReportVideoOnLoad: checked });
  };

  return (
    <>
      <FormControlLabel
        control={<Switch onChange={toggleAutoPlayOnLoad} />}
        checked={userMetadata?.autoPlayMeetingReportVideoOnLoad ?? true}
        disabled={!playbackEnabled}
        label={
          <Row variant="left" spacing={1}>
            <SwitchLabel>Auto-play video on load</SwitchLabel>
            <TooltipIcon
              placement="bottom"
              title="Video will automatically play on page load"
            />
          </Row>
        }
      />
      <FormControlLabel
        control={<Switch onChange={toggleAutoPlay} />}
        checked={playbackEnabled && autoPlay}
        disabled={!playbackEnabled}
        label={
          <Row variant="left" spacing={1}>
            <SwitchLabel>Auto-play video on click</SwitchLabel>
            <TooltipIcon
              placement="bottom"
              title="Video will automatically play when clicking on notes, transcript turns, and highlights"
            />
          </Row>
        }
      />
      <FormControlLabel
        control={<Switch onChange={toggleReactionsEnabled} />}
        checked={reactionsEnabled}
        label={<SwitchLabel>Highlight reactions in transcript</SwitchLabel>}
      />
      <MenuDivider />
      <Stack
        direction={{ xxs: "column", xs: "row" }}
        alignItems={{ xs: "center" }}
        justifyContent={{ xs: "space-between" }}
        spacing={{ xxs: 1, xs: 3 }}
      >
        <Typography noWrap>Text size</Typography>
        <Row spacing={1}>
          <ToggleButtonGroup
            value={textSize}
            onChange={handleTextSizeChange}
            exclusive
          >
            <ToggleButton value="0.875em" sx={{ fontSize: "0.875em" }}>
              small
            </ToggleButton>
            <ToggleButton value="1em" sx={{ fontSize: "1em" }}>
              default
            </ToggleButton>
            <ToggleButton value="1.125em" sx={{ fontSize: "1.125em" }}>
              large
            </ToggleButton>
          </ToggleButtonGroup>
        </Row>
      </Stack>
    </>
  );
};

export default RecapSettingsContent;
